import { useQuery } from "@tanstack/react-query";
import { z } from "zod";

import { apiGet } from "@/api/client";

/**
 * GET /api/v2/teslimat/haftalik — haftalik teslimat ChartCard'i icin
 * (docs/faz5-arayuz-spec.md §2/§4, backend/app/core/api/orders.py). Bu tek
 * uc icin ayri bir api/ dosyasi yerine ozet ozelinde tutulur (F3/F4'un
 * sahiplendigi api/* dosyalariyla cakismasin diye).
 */
export const teslimatHaftaSchema = z
  .object({
    yil: z.string(),
    hafta_no: z.number(),
    hafta_baslangic: z.string(),
    ort_saniye: z.number().nullable(),
    min_saniye: z.number().nullable(),
    max_saniye: z.number().nullable(),
    adet: z.number(),
  })
  .passthrough();
export type TeslimatHafta = z.infer<typeof teslimatHaftaSchema>;

export const teslimatHaftalikResponseSchema = z.object({
  ready: z.boolean(),
  haftalar: z.array(teslimatHaftaSchema),
  ozet: z
    .object({
      genel_ort_saniye: z.number().nullable(),
      olculen_siparis: z.number(),
      teslim_edilen_toplam: z.number(),
      veri_kapsami_pct: z.number(),
    })
    .passthrough(),
});
export type TeslimatHaftalikResponse = z.infer<typeof teslimatHaftalikResponseSchema>;

export function fetchTeslimatHaftalik(hafta = 16): Promise<TeslimatHaftalikResponse> {
  return apiGet(`/api/v2/teslimat/haftalik?hafta=${hafta}`, teslimatHaftalikResponseSchema);
}

export function useTeslimatHaftalik(hafta = 16) {
  return useQuery({
    queryKey: ["teslimat", "haftalik", hafta],
    queryFn: () => fetchTeslimatHaftalik(hafta),
  });
}

/**
 * "3g 4s" / "3 gün 4 saat" bicimi — eski `frontend/legacy/js/app.js:829-840`
 * `fmtSure()` portu (gorev C). `uzun=false` kisa (etiket/eksene), `uzun=true`
 * uzun (cip/tooltip) bicim.
 */
export function fmtSure(saniye: number | null | undefined, uzun = false): string {
  const s = Number(saniye);
  if (!Number.isFinite(s) || s <= 0) return "—";
  const totalSaat = s / 3600;
  const gun = Math.floor(totalSaat / 24);
  const saat = Math.round(totalSaat % 24);
  if (uzun) {
    if (gun <= 0) return `${saat} saat`;
    return saat > 0 ? `${gun} gün ${saat} saat` : `${gun} gün`;
  }
  if (gun <= 0) return `${saat}s`;
  return saat > 0 ? `${gun}g ${saat}s` : `${gun}g`;
}

/**
 * GET /api/v2/teslimat/hafta?yil=&hafta= — bir haftanin teslim edilmis
 * siparisleri (gorev C, TeslimatHaftaModal.tsx). Backend
 * `backend/app/core/api/orders.py teslimat_hafta_endpoint`.
 */
export const teslimatSiparisSchema = z
  .object({
    siparis_id: z.union([z.string(), z.number()]).nullable().optional(),
    order_no: z.string().nullable().optional(),
    customer_name: z.string().nullable().optional(),
    amount_try: z.number().nullable().optional(),
    cargo_company: z.string().nullable().optional(),
    siparis_tarihi: z.string().nullable().optional(),
    teslim_tarihi: z.string().nullable().optional(),
    sure_saniye: z.number().nullable().optional(),
    /** adapter.admin_link("order", …) — yoksa DeepLink duz metin gosterir. */
    admin_link: z.string().nullable().optional(),
  })
  .passthrough();
export type TeslimatSiparis = z.infer<typeof teslimatSiparisSchema>;

export const teslimatHaftaDetayResponseSchema = z.object({
  yil: z.number(),
  hafta: z.number(),
  adet: z.number(),
  ort_saniye: z.number().nullable(),
  siparisler: z.array(teslimatSiparisSchema),
});
export type TeslimatHaftaDetayResponse = z.infer<typeof teslimatHaftaDetayResponseSchema>;

export interface TeslimatHaftaSecimi {
  yil: string | number;
  hafta_no: number;
}

export function fetchTeslimatHafta(secim: TeslimatHaftaSecimi): Promise<TeslimatHaftaDetayResponse> {
  return apiGet(
    `/api/v2/teslimat/hafta?yil=${encodeURIComponent(String(secim.yil))}&hafta=${encodeURIComponent(String(secim.hafta_no))}`,
    teslimatHaftaDetayResponseSchema,
  );
}

export function useTeslimatHafta(secim: TeslimatHaftaSecimi | null) {
  return useQuery({
    queryKey: ["teslimat", "hafta", secim?.yil, secim?.hafta_no] as const,
    queryFn: () => fetchTeslimatHafta(secim as TeslimatHaftaSecimi),
    enabled: secim != null,
  });
}
